/**
 * LegalConsentGate.jsx
 *
 * Blocking, one-shot confirmation of the current Terms + Privacy version
 * for users who never went through the checkout consent flow (or who
 * accepted an earlier terms_version). Rendered INSIDE ProtectedRoute so it
 * only ever gates authenticated screens; login / public pages stay
 * untouched.
 *
 * Flow:
 *   1. mount → GET /api/legal-consent/status
 *   2. if `accepted === true` → render children (never flash the modal).
 *   3. if `accepted === false` → render blocking modal until the user
 *      ticks the checkbox and hits the CTA. POST /legal-consent/accept
 *      flips local state to accepted and the children render — no reload.
 *   4. if the status call errors → fail OPEN (render children) so a
 *      transient backend blip never locks users out of paid features.
 *
 * Links open the EXISTING Terms / Privacy content on the pricing page in
 * a new tab — same routing as the checkout consent checkboxes. Never
 * duplicated here.
 */

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function LegalConsentGate({ children }) {
  const { lang } = useAuth();
  const isEN = lang === "en";

  // "loading" gates rendering so a user with a valid consent NEVER sees
  // even a flash of the modal.
  const [phase, setPhase] = useState("loading"); // loading | ok | needs
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/legal-consent/status");
        if (cancelled) return;
        if (r?.data?.accepted === true) setPhase("ok");
        else setPhase("needs");
      } catch {
        // Fail open — a transient status failure must not block LEO.
        if (!cancelled) setPhase("ok");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onAccept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/legal-consent/accept", { locale: lang });
      if (r?.data?.accepted === true) setPhase("ok");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-stone-950"
        data-testid="legal-consent-loading"
      />
    );
  }

  if (phase === "ok") return children;

  // phase === "needs" — blocking modal.
  const title = isEN ? "Confirm LEO documents" : "Potwierdź dokumenty LEO";
  const body = isEN
    ? "To continue using LEO, please review the current Terms and Conditions and Privacy Policy."
    : "Aby kontynuować korzystanie z LEO, zapoznaj się z aktualnym Regulaminem i Polityką Prywatności.";
  const cta = isEN
    ? "Accept and continue to LEO"
    : "Akceptuję i przechodzę do LEO";

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-stone-950/90 backdrop-blur-sm p-4"
      data-testid="legal-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-consent-title"
    >
      <div className="w-full max-w-lg rounded-2xl border border-stone-800 bg-stone-900 p-6 sm:p-8 shadow-2xl">
        <h2
          id="legal-consent-title"
          className="serif text-2xl text-stone-100 mb-3"
          data-testid="legal-consent-title"
        >
          {title}
        </h2>
        <p className="text-sm text-stone-300 leading-relaxed mb-5">{body}</p>

        <label
          className="flex items-start gap-2 text-sm text-stone-200 leading-relaxed cursor-pointer mb-6"
          data-testid="legal-consent-checkbox-label"
        >
          <input
            type="checkbox"
            data-testid="legal-consent-checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 shrink-0"
          />
          <span>
            {isEN ? (
              <>
                I accept the{" "}
                <a
                  href="/pricing#terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-500 underline"
                  data-testid="legal-consent-terms-link"
                >
                  LEO Terms and Conditions
                </a>{" "}
                and confirm that I have read the{" "}
                <a
                  href="/pricing#privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-500 underline"
                  data-testid="legal-consent-privacy-link"
                >
                  Privacy Policy
                </a>
                .
              </>
            ) : (
              <>
                Akceptuję{" "}
                <a
                  href="/pricing#terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-500 underline"
                  data-testid="legal-consent-terms-link"
                >
                  Regulamin LEO
                </a>{" "}
                i potwierdzam zapoznanie się z{" "}
                <a
                  href="/pricing#privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-500 underline"
                  data-testid="legal-consent-privacy-link"
                >
                  Polityką Prywatności
                </a>
                .
              </>
            )}
          </span>
        </label>

        <button
          type="button"
          onClick={onAccept}
          disabled={!checked || submitting}
          data-testid="legal-consent-accept-btn"
          className="pill-btn w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {cta}
        </button>
      </div>
    </div>
  );
}
